import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Box from "@mui/material/Box";
import { enqueueSnackbar } from "notistack";
import React, { ReactElement, useState } from "react";

import { Asset } from "../../../generated/gql/graphql";
import useAssets from "../../../hooks/mutation/assets/use-assets";
import useText from "../../../hooks/use-text";
import { LocalizedStrings, SupportedLanguages } from "../../../utils/lang";

type Props = {
  assets: Asset[];
  open: boolean;
  onClose(): void;
};

export default function ArchiveAssets(props: Props): ReactElement {
  const { assets, open, onClose } = props;

  const [isLoading, setIsLoading] = useState(false);
  const { archiveAssets: archiveAssetsMut } = useAssets();

  const { strings } = useText(local);

  async function archiveAssets(): Promise<void> {
    setIsLoading(true);
    try {
      await archiveAssetsMut(assets.map((asset) => asset.id as string));
    } catch (error) {
      console.error("Failed to archive assets", error);
      enqueueSnackbar(strings.error, { variant: "error" });
    } finally {
      setIsLoading(false);
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {strings.title.replace("{{count}}", assets.length.toString())}
      </DialogTitle>
      <DialogContent className="min-w-[300px]">
        <ul className="max-h-40 space-y-5 bg-gray-100 p-5">
          {assets.map((asset: Asset) => (
            <li key={asset.id}>
              <p className="text-sm">{asset.name}</p>
            </li>
          ))}
        </ul>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings.cancel}</Button>
        <Button onClick={archiveAssets}>
          {isLoading ? (
            <Box className="flex justify-center">
              <CircularProgress size="1rem" className="w-1 h-1" />
            </Box>
          ) : (
            strings.download
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type LocalSchema = {
  title: string;
  cancel: string;
  download: string;
  error: string;
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    title: "Archive Assets ({{count}})",
    cancel: "Cancel",
    download: "Archive",
    error: "Failed to archive assets. Please try it again",
  },
};
